import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '../ui/Button';
import { theme } from '../../theme';
import { cn } from '../../lib/utils';
import { reportReactCrash } from '../../infrastructure/monitoring';

interface Props {
  children?: ReactNode;
  /** Custom fallback UI — rendered when an error is caught. */
  fallback?: ReactNode;
  /** onError callback for external error reporting (e.g. Sentry/telemetry). */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
    reportReactCrash(error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return (
          <div>
            {React.isValidElement(this.props.fallback)
              ? React.cloneElement(this.props.fallback as React.ReactElement<any>, {
                  error: this.state.error,
                })
              : this.props.fallback}
            {this.state.error && (
              <div className="max-w-2xl mx-auto p-4 mt-2 bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-800 rounded-2xl text-left">
                <p className="text-xs font-bold text-rose-700 dark:text-rose-300 font-mono">
                  {this.state.error.name}: {this.state.error.message}
                </p>
                {this.state.error.stack && (
                  <pre className="text-[10px] text-rose-600 dark:text-rose-400 mt-2 overflow-x-auto whitespace-pre-wrap font-mono max-h-48">
                    {this.state.error.stack}
                  </pre>
                )}
              </div>
            )}
          </div>
        );
      }

      return (
        <div className={theme.layout.flexCenter + " min-h-[400px] p-8"}>
          <div className={cn(theme.components.card, "max-w-md w-full p-8 text-center space-y-6 shadow-xl border-rose-100/50")}>
            <div className={cn(theme.components.iconBadge.danger, "w-16 h-16 rounded-2xl mx-auto shadow-sm")}>
              <AlertTriangle size={32} />
            </div>
            
            <div className="space-y-2">
              <h2 className={theme.typography.h2}>Something went wrong</h2>
              <p className={theme.typography.body}>
                An unexpected error occurred while rendering this component. Our team has been notified.
              </p>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="p-4 bg-slate-50 rounded-xl text-left overflow-auto max-h-40 border border-slate-100">
                <p className="text-[10px] font-mono text-rose-600 break-all font-medium">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <Button 
              onClick={this.handleReset}
              className="w-full justify-center"
              icon={<RotateCcw size={18} />}
            >
              Reload Application
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
